import { useCallback, useEffect, useRef, useState } from 'react';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { assinarFluxo, socketDoPainel } from '../socket/painel';
import {
  guardarSegmento,
  juntarSegmentos,
  type SegmentoDeDitado,
} from '../componentes/chat/ditado';
import { Gravador, type EstadoDoGravador } from './gravador';

export interface DitadoAoVivo {
  gravando: boolean;
  /** RMS do quadro, para quem desenha medidor ou orbe. */
  nivel: number;
  /** O texto que já voltou, crescendo enquanto a pessoa fala. */
  parcial: string;
  /** Trechos enviados e ainda sem resposta. */
  pendentes: number;
  /** Quanto ja foi gravado, para o cronometro. */
  duracaoMs: number;
  erro: string;
  comecar: () => Promise<void>;
  /** Para, espera o último trecho e devolve o texto inteiro. */
  terminar: () => Promise<string>;
}

/**
 * Ditado por voz com o texto aparecendo enquanto se fala.
 *
 * A esteira toda já existia — `Gravador`, trechos em WAV 16 kHz, fila na
 * IndexedDB, `transcricao.segmento` chegando pelo fluxo `gravacao:<id>` com
 * `seq` e replay. O que faltava era **mostrar**: o `BotaoDeDitado` acumulava os
 * segmentos num `useRef` e só olhava para eles no fim. Aqui eles vão para
 * estado, e a diferença de código é literalmente essa.
 *
 * Mora em `audio/`, e não em `prosa/`, de propósito: é aqui que a fiação de
 * socket pode morar. `prosa/arquitetura.spec.ts` recusa `assinarFluxo` do lado
 * da tela, e essa recusa é o que impede o replay de nascer duas vezes.
 *
 * O que NÃO se faz aqui: inventar palpite entre um segmento e outro. Enquanto
 * o primeiro trecho não volta, `parcial` fica vazio e quem desenha mostra o
 * orbe e o relógio. Texto inventado numa tela de ditado jurídico é pior que
 * tela parada.
 */
export function usarDitadoAoVivo(sessaoTitulo = 'Ditado na Prosa'): DitadoAoVivo {
  const [estado, setEstado] = useState<EstadoDoGravador | null>(null);
  const [segmentos, setSegmentos] = useState<SegmentoDeDitado[]>([]);
  const [erro, setErro] = useState('');

  const gravador = useRef<Gravador | null>(null);
  const gravacaoId = useRef('');
  const cursor = useRef(0);
  const cancelarAssinatura = useRef<(() => void) | null>(null);
  /** O ouvinte exato, para tirar só ele — `off` sem handler limpa todos. */
  const ouvinte = useRef<((s: SegmentoDeDitado & { fluxo?: string }) => void) | null>(null);

  const soltar = useCallback((): void => {
    const socket = socketDoPainel();
    if (ouvinte.current) socket.off('transcricao.segmento', ouvinte.current);
    ouvinte.current = null;
    cancelarAssinatura.current?.();
    cancelarAssinatura.current = null;
  }, []);

  // Sair da tela no meio de um ditado tem de desligar o microfone: uma luz
  // vermelha acesa depois de a pessoa fechar a conversa é falha grave.
  useEffect(
    () => () => {
      const vivo = gravador.current;
      gravador.current = null;
      soltar();
      void vivo?.parar().catch(() => undefined);
    },
    [soltar],
  );

  const comecar = useCallback(async (): Promise<void> => {
    if (gravador.current) return;
    setErro('');
    setSegmentos([]);
    cursor.current = 0;

    const socket = socketDoPainel();
    const motor = new Gravador((novo) => setEstado(novo), { semTomadaContinua: true });
    gravador.current = motor;

    const aoChegar = (segmento: SegmentoDeDitado & { fluxo?: string }): void => {
      // O socket é um só para o painel inteiro: sem filtrar pelo fluxo, o
      // ditado colheria segmento de qualquer gravação aberta em outra aba.
      if (segmento.fluxo && segmento.fluxo !== 'gravacao:' + gravacaoId.current) return;
      if (segmento.seq > cursor.current) cursor.current = segmento.seq;
      // `guardarSegmento` devolve a MESMA lista quando o `seq` já existe, então
      // a reentrega do replay não repinta a tela à toa.
      setSegmentos((atuais) => guardarSegmento(atuais, segmento));
    };

    try {
      const id = await motor.iniciar(sessaoTitulo);
      gravacaoId.current = id;
      ouvinte.current = aoChegar;
      socket.on('transcricao.segmento', aoChegar);
      cancelarAssinatura.current = assinarFluxo('gravacao:' + id, () => cursor.current);
    } catch (falha) {
      gravador.current = null;
      setEstado(null);
      soltar();
      const nome = (falha as Error).name;
      setErro(
        nome === 'NotAllowedError'
          ? 'o navegador negou o microfone para este site'
          : nome === 'NotFoundError'
            ? 'nenhum microfone encontrado nesta máquina'
            : 'não consegui abrir o microfone (' + (nome || 'erro') + ')',
      );
    }
  }, [sessaoTitulo, soltar]);

  const terminar = useCallback(async (): Promise<string> => {
    const motor = gravador.current;
    if (!motor) return '';
    gravador.current = null;
    const id = gravacaoId.current;

    let colhidos: SegmentoDeDitado[] = [];
    try {
      await motor.parar();
      // O último trecho só é transcrito depois do `finalizar`. Espera curta e
      // com teto: melhor entregar o que veio do que travar a tela.
      for (let tentativa = 0; tentativa < 20; tentativa += 1) {
        const pagina = await chamar<{ itens: SegmentoDeDitado[] }>(
          `${ROTAS.gravacoes}/${id}/segmentos?limite=200`,
        );
        colhidos = pagina.itens;
        setSegmentos(pagina.itens);
        const gravacao = await chamar<{ estado: string }>(`${ROTAS.gravacoes}/${id}`);
        if (gravacao.estado === 'CONCLUIDA' || gravacao.estado === 'ERRO') break;
        await new Promise((r) => setTimeout(r, 700));
      }
    } catch {
      setErro('a transcrição não voltou');
    } finally {
      soltar();
      // Ditado não é acervo: a gravação sai da lista de áudio.
      void chamar(`${ROTAS.gravacoes}/${id}`, { method: 'DELETE' }).catch(() => undefined);
      setEstado(null);
      /*
        E os segmentos vão junto. O texto já foi devolvido a quem chamou
        (`colhidos`), e guardá-los aqui deixava `parcial` cheio depois do fim —
        foi assim que a Prosa ficou presa na tela do orbe para sempre, porque
        quem esvaziava era `comecar()`, na gravação SEGUINTE. Texto de uma fala
        que acabou não pode sobrar como parcial de nada.
      */
      setSegmentos([]);
    }
    return juntarSegmentos(colhidos);
  }, [soltar]);

  return {
    gravando: Boolean(estado?.gravando),
    nivel: estado?.nivel ?? 0,
    parcial: juntarSegmentos(segmentos),
    pendentes: estado?.pendentes ?? 0,
    duracaoMs: estado?.duracaoMs ?? 0,
    erro,
    comecar,
    terminar,
  };
}
